import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api, { getApiErrorMessage } from '../services/api';
import InteractiveTable from '../components/InteractiveTable';
import {
  Box, Plus, Search, Filter, ChevronDown, ChevronUp, ArrowUpDown,
  Settings2, BarChart3, Layers, PenLine, Trash2, Tag, DollarSign,
  Building2, UserCheck, ArrowRightLeft, Undo2, Wrench, XCircle,
  Calendar, Hash, FileText, ClipboardList, Clock, TrendingDown
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

import ModuleReports from './Reports/ModuleReports';
import { ASSET_REPORTS } from './Reports/reportConfigs';

const DEPR_LABELS = { straight_line: 'Straight Line', reducing_balance: 'Reducing Balance', none: 'None' };
const PIE_COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_storage: 'bg-amber-100 text-amber-700',
  in_maintenance: 'bg-orange-100 text-orange-700',
  disposed: 'bg-red-100 text-red-700',
  written_off: 'bg-red-200 text-red-800',
};

const STATUS_LABELS = {
  draft: 'Draft', active: 'Active', assigned: 'Assigned',
  in_storage: 'In Storage', in_maintenance: 'In Maintenance',
  disposed: 'Disposed', written_off: 'Written Off',
};

export default function AssetManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { systemSettings } = useAuth();
  const cur = systemSettings?.system_currency || 'USD';
  const tab = searchParams.get('tab') || 'dashboard';
  const detailAsset = searchParams.get('detail') || '';

  const setTab = (t) => setSearchParams({ tab: t });

  if (detailAsset) return <AppShell><AssetDetail assetNumber={detailAsset} onBack={() => setSearchParams({ tab: 'register' })} /></AppShell>;

  const tabs = [
    { id: 'dashboard', label: 'Analytics', icon: BarChart3 },
    { id: 'register', label: 'Asset Register', icon: Layers },
    { id: 'classes', label: 'Asset Classes', icon: Settings2 },
    { id: 'reports', label: 'Reports', icon: FileText },
  ];

  return (
    <AppShell>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }} data-testid="asset-page-title">Asset Management</h1>
            <HelpButton guideId="asset-register" label="Asset Register Guide" />
          </div>
        </div>
        <div className="flex gap-1 border-b" data-testid="asset-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`asset-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === 'register' && <AssetRegister onViewDetail={(n) => setSearchParams({ detail: n })} />}
        {tab === 'classes' && <AssetClasses />}
        {tab === 'dashboard' && <AssetDashboard onNavigate={(params) => setSearchParams(params)} />}
        {tab === 'reports' && <ModuleReports reports={ASSET_REPORTS} apiPrefix="/api/module-reports/assets" moduleLabel="Asset Management" />}
      </div>
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  ASSET REGISTER TAB
// ═══════════════════════════════════════════════════════════════════════

function AssetRegister({ onViewDetail }) {
  const [searchParams] = useSearchParams();
  const { systemSettings } = useAuth();
  const cur = systemSettings?.system_currency || 'USD';
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    class_code: searchParams.get('class_code') || '',
    status: searchParams.get('status') || '',
    site_code: searchParams.get('site_code') || '',
    asset_type: '',
  });
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [sortState, setSortState] = useState({ field: 'asset_number', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [classes, setClasses] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50, search });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const [assetsRes, classesRes] = await Promise.all([
        api.get(`/api/assets?${params}`),
        api.get('/api/assets/classes'),
      ]);
      setAssets(assetsRes.data.data || []);
      setTotal(assetsRes.data.total || 0);
      setClasses(classesRes.data.data || []);
    } catch { toast.error('Failed to load assets'); }
    setLoading(false);
  }, [page, search, filters]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const arr = [...assets];
    arr.sort((a, b) => {
      const av = a[sortState.field] ?? '', bv = b[sortState.field] ?? '';
      const dir = sortState.dir === 'asc' ? 1 : -1;
      if (typeof av === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return arr;
  }, [assets, sortState]);

  const assetDefaultCols = useMemo(() => [
    { key: 'asset_number', label: 'Asset #', width: 120, sortField: 'asset_number', visible: true },
    { key: 'description', label: 'Description', width: 220, sortField: 'description', visible: true },
    { key: 'class_name', label: 'Class', width: 130, sortField: 'class_name', visible: true },
    { key: 'status', label: 'Status', width: 120, sortField: 'status', visible: true },
    { key: 'serial_number', label: 'Serial #', width: 130, sortField: 'serial_number', visible: true },
    { key: 'assigned_user_name', label: 'Assigned To', width: 150, sortField: 'assigned_user_name', visible: true },
    { key: 'site_name', label: 'Site', width: 130, sortField: 'site_name', visible: true },
    { key: 'acquisition_cost', label: `Cost (${cur})`, width: 120, sortField: 'acquisition_cost', align: 'right', visible: true },
    { key: 'net_book_value', label: `NBV (${cur})`, width: 120, sortField: 'net_book_value', align: 'right', visible: true },
  ], [cur]);

  const renderAssetCell = useCallback((col, a) => {
    const fmtCurr = (v) => Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    switch (col.key) {
      case 'asset_number': return <span className="font-mono text-sm font-medium">{a.asset_number}</span>;
      case 'description': return <span className="text-sm max-w-[200px] truncate block">{a.description}</span>;
      case 'class_name': return <span className="text-sm">{a.class_name}</span>;
      case 'status': return <Badge className={`text-[10px] ${STATUS_COLORS[a.status] || ''}`}>{STATUS_LABELS[a.status] || a.status}</Badge>;
      case 'serial_number': return <span className="text-sm font-mono">{a.serial_number || '—'}</span>;
      case 'assigned_user_name': return <span className="text-sm">{a.assigned_user_name || '—'}</span>;
      case 'site_name': return <span className="text-sm">{a.site_name || '—'}</span>;
      case 'acquisition_cost': return <span className="text-sm tabular-nums">{fmtCurr(a.acquisition_cost)}</span>;
      case 'net_book_value': return <span className="text-sm tabular-nums">{fmtCurr(a.net_book_value)}</span>;
      default: return <span className="text-sm">{a[col.key] != null ? String(a[col.key]) : '—'}</span>;
    }
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search assets..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9" data-testid="asset-search" />
        </div>
        <select value={filters.class_code} onChange={e => setFilters(p => ({ ...p, class_code: e.target.value }))}
          className="h-9 rounded-lg border px-2 text-sm bg-background" data-testid="filter-class">
          <option value="">All Classes</option>
          {classes.map(c => <option key={c.class_code} value={c.class_code}>{c.class_name}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}
          className="h-9 rounded-lg border px-2 text-sm bg-background" data-testid="filter-status">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.asset_type} onChange={e => setFilters(p => ({ ...p, asset_type: e.target.value }))}
          className="h-9 rounded-lg border px-2 text-sm bg-background" data-testid="filter-type">
          <option value="">All Types</option>
          <option value="fixed_asset">Fixed Asset</option>
          <option value="current_asset">Current Asset</option>
        </select>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowBulk(true)} data-testid="bulk-create-btn">
            <ClipboardList className="h-4 w-4 mr-1" /> Bulk Create
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-asset-btn">
            <Plus className="h-4 w-4 mr-1" /> New Asset
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No assets found</div>
        ) : (
          <InteractiveTable
            tableId="asset-register"
            defaultCols={assetDefaultCols}
            data={sorted}
            renderCell={renderAssetCell}
            onRowClick={(row) => onViewDetail(row.asset_number)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="asset_number"
          />
        )}
      </div>
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{total} asset{total !== 1 ? 's' : ''}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="px-2 py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={assets.length < 50} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      </div>

      {showCreate && <CreateAssetDialog classes={classes} onClose={() => setShowCreate(false)} onCreated={load} />}
      {showBulk && <BulkCreateDialog classes={classes} onClose={() => setShowBulk(false)} onCreated={load} />}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  ASSET DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════════

function AssetDetail({ assetNumber, onBack }) {
  const [asset, setAsset] = useState(null);
  const [tab, setTab] = useState('summary');
  const [schedule, setSchedule] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null); // 'assign'|'transfer'|'return'|'maintenance'|'dispose'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, dRes, hRes, eRes] = await Promise.all([
        api.get(`/api/assets/${assetNumber}`),
        api.get(`/api/assets/${assetNumber}/depreciation`),
        api.get(`/api/assets/${assetNumber}/assignments`),
        api.get(`/api/assets/${assetNumber}/events`),
      ]);
      setAsset(aRes.data);
      setSchedule(dRes.data.data || []);
      setAssignments(hRes.data.data || []);
      setEvents(eRes.data.data || []);
    } catch { toast.error('Failed to load asset'); }
    setLoading(false);
  }, [assetNumber]);

  useEffect(() => { load(); }, [load]);

  if (loading || !asset) return <div className="text-center py-12 text-muted-foreground">Loading asset...</div>;

  const isDisposed = ['disposed', 'written_off'].includes(asset.status);
  const fmt = (v) => Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const detailTabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'financials', label: 'Financials', icon: DollarSign },
    { id: 'depreciation', label: 'Depreciation', icon: BarChart3 },
    { id: 'assignments', label: 'Assignments', icon: UserCheck },
    { id: 'audit', label: 'Audit Trail', icon: Clock },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="asset-back-btn">&larr; Back</Button>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{asset.asset_number}</h2>
          <Badge className={STATUS_COLORS[asset.status]}>{STATUS_LABELS[asset.status]}</Badge>
        </div>
        {!isDisposed && (
          <div className="flex gap-2" data-testid="asset-actions">
            <Button size="sm" variant="outline" onClick={() => setActionDialog('assign')} data-testid="action-assign"><UserCheck className="h-3.5 w-3.5 mr-1" /> Assign</Button>
            <Button size="sm" variant="outline" onClick={() => setActionDialog('transfer')} data-testid="action-transfer"><ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Transfer</Button>
            <Button size="sm" variant="outline" onClick={() => setActionDialog('return')} data-testid="action-return"><Undo2 className="h-3.5 w-3.5 mr-1" /> Return</Button>
            <Button size="sm" variant="outline" onClick={() => setActionDialog('maintenance')} data-testid="action-maintenance"><Wrench className="h-3.5 w-3.5 mr-1" /> Maintenance</Button>
            <Button size="sm" variant="destructive" onClick={() => setActionDialog('dispose')} data-testid="action-dispose"><XCircle className="h-3.5 w-3.5 mr-1" /> Dispose</Button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b">
        {detailTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`detail-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="asset-summary">
          {[
            ['Description', asset.description],
            ['Class', `${asset.class_name} (${asset.class_code})`],
            ['Asset Type', asset.asset_type === 'fixed_asset' ? 'Fixed Asset' : 'Current Asset'],
            ['Status', STATUS_LABELS[asset.status]],
            ['Serial Number', asset.serial_number || '—'],
            ['Tag Number', asset.tag_number || '—'],
            ['Site', asset.site_name || '—'],
            ['Location', asset.location || '—'],
            ['Assigned To', asset.assigned_user_name || '—'],
            ['Acquisition Date', asset.acquisition_date || '—'],
            ['In-Service Date', asset.in_service_date || '—'],
            ['Notes', asset.notes || '—'],
          ].map(([l, v]) => (
            <div key={l} className="space-y-0.5">
              <p className="text-xs text-muted-foreground font-medium">{l}</p>
              <p className="text-sm">{v}</p>
            </div>
          ))}
          {asset.source_type && asset.source_type !== 'manual' && (
            <>
              <div className="col-span-full border-t pt-3 mt-2"><p className="text-xs font-semibold text-muted-foreground">Source References</p></div>
              {[
                ['Source Type', asset.source_type],
                ['Source Document', asset.source_document_number || '—'],
                ['Source Item', asset.source_item_code || '—'],
                ['Source Order', asset.source_order_number || '—'],
                ['Source Invoice', asset.source_invoice_number || '—'],
              ].map(([l, v]) => (
                <div key={l} className="space-y-0.5">
                  <p className="text-xs text-muted-foreground font-medium">{l}</p>
                  <p className="text-sm">{v}</p>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'financials' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="asset-financials">
          {[
            ['Acquisition Cost', fmt(asset.acquisition_cost)],
            ['Depreciation Method', DEPR_LABELS[asset.depreciation_method] || asset.depreciation_method || 'None'],
            ['Useful Life', asset.useful_life_months ? `${asset.useful_life_months} months` : '—'],
            ['Residual Value', fmt(asset.residual_value)],
            ['Depreciation Start', asset.depreciation_start_date || '—'],
            ['Accumulated Depreciation', fmt(asset.accumulated_depreciation)],
            ['Net Book Value', fmt(asset.net_book_value)],
          ].map(([l, v]) => (
            <div key={l} className="p-3 border rounded-lg">
              <p className="text-xs text-muted-foreground font-medium">{l}</p>
              <p className="text-lg font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'depreciation' && (
        <div className="border rounded-lg overflow-hidden" data-testid="depreciation-schedule">
          {schedule.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No depreciation schedule (method: none)</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Period</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Accumulated</TableHead><TableHead className="text-right">NBV</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {schedule.map(e => (
                  <TableRow key={e.period}>
                    <TableCell className="text-sm">{e.period}</TableCell>
                    <TableCell className="text-sm">{e.period_date ? new Date(e.period_date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', timeZone: 'America/Los_Angeles' }) : ''}</TableCell>
                    <TableCell className="text-sm text-right">{fmt(e.depreciation_amount)}</TableCell>
                    <TableCell className="text-sm text-right">{fmt(e.accumulated_depreciation)}</TableCell>
                    <TableCell className="text-sm text-right font-medium">{fmt(e.net_book_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {tab === 'assignments' && (
        <div className="border rounded-lg overflow-hidden" data-testid="assignment-history">
          {assignments.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No assignment history</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Action</TableHead><TableHead>User</TableHead><TableHead>Site</TableHead><TableHead>Date</TableHead><TableHead>By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {assignments.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell><Badge variant="outline" className="capitalize text-[10px]">{a.action}</Badge></TableCell>
                    <TableCell className="text-sm">{a.user_name || a.from_user_name || '—'}</TableCell>
                    <TableCell className="text-sm">{a.site_name || '—'}</TableCell>
                    <TableCell className="text-sm">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</TableCell>
                    <TableCell className="text-sm">{a.performed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="space-y-2" data-testid="audit-trail">
          {events.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No events recorded</p>
          ) : events.map((e, i) => (
            <div key={i} className="flex items-start gap-3 p-3 border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{e.event_type}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(e.timestamp).toLocaleString()}</span>
                </div>
                <p className="text-sm mt-0.5">{e.description}</p>
                <p className="text-xs text-muted-foreground">by {e.performed_by_name || e.performed_by}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {actionDialog && (
        <ActionDialog
          type={actionDialog}
          asset={asset}
          onClose={() => setActionDialog(null)}
          onDone={() => { setActionDialog(null); load(); }}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  ASSET CLASSES TAB
// ═══════════════════════════════════════════════════════════════════════

function AssetClasses() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editClass, setEditClass] = useState(null);
  const [sort, setSort] = useState({ field: 'class_code', dir: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/assets/classes');
      setClasses(r.data.data || []);
    } catch { toast.error('Failed to load classes'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const arr = [...classes];
    arr.sort((a, b) => {
      const av = a[sort.field] ?? '', bv = b[sort.field] ?? '';
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
    return arr;
  }, [classes, sort]);

  const toggleSort = (field) => setSort(p => ({ field, dir: p.field === field ? -p.dir : 1 }));
  const SortIcon = ({ field }) => sort.field === field ? (sort.dir === 1 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />;
  const SH = ({ field, label }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <span className="flex items-center gap-1">{label} <SortIcon field={field} /></span>
    </TableHead>
  );

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete asset class "${code}"?`)) return;
    try {
      await api.delete(`/api/assets/classes/${code}`);
      toast.success('Class deleted');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Define asset classes with depreciation rules. Assets inherit these settings at creation time.</p>
        <Button size="sm" onClick={() => setEditClass({})} data-testid="create-class-btn"><Plus className="h-4 w-4 mr-1" /> New Class</Button>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <SH field="class_code" label="Code" />
            <SH field="class_name" label="Name" />
            <SH field="asset_type" label="Type" />
            <SH field="depreciation_method" label="Depreciation" />
            <SH field="useful_life_months" label="Life (months)" />
            <TableHead>Residual</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="w-[80px]">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : sorted.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No asset classes. Create one to get started.</TableCell></TableRow>
            ) : sorted.map(c => (
              <TableRow key={c.class_code} data-testid={`class-row-${c.class_code}`}>
                <TableCell className="font-mono text-sm font-medium">{c.class_code}</TableCell>
                <TableCell className="text-sm">{c.class_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{c.asset_type === 'fixed_asset' ? 'Fixed' : 'Current'}</Badge></TableCell>
                <TableCell className="text-sm capitalize">{c.depreciation_method?.replace('_', ' ')}</TableCell>
                <TableCell className="text-sm text-center">{c.useful_life_months || '—'}</TableCell>
                <TableCell className="text-sm">{c.residual_value_type === 'percentage' ? `${c.residual_value_default}%` : c.residual_value_type === 'amount' ? `$${c.residual_value_default}` : '—'}</TableCell>
                <TableCell>{c.active ? <Badge className="bg-green-100 text-green-700 text-[10px]">Yes</Badge> : <Badge className="bg-gray-100 text-gray-500 text-[10px]">No</Badge>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditClass(c)} data-testid={`edit-class-${c.class_code}`}><PenLine className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(c.class_code)} data-testid={`delete-class-${c.class_code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {editClass !== null && <ClassFormDialog initial={editClass} onClose={() => setEditClass(null)} onSaved={load} />}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════════════

function AssetDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/api/assets/summary').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center py-12 text-muted-foreground">Loading dashboard...</div>;

  const fmt = (v) => `$${Number(v || 0).toLocaleString()}`;
  const kpis = [
    { label: 'Total Assets', value: data.total_assets || 0, icon: Box, color: 'text-indigo-600', action: () => onNavigate({ tab: 'register' }) },
    { label: 'Acquisition Value', value: fmt(data.total_acquisition_value), icon: DollarSign, color: 'text-emerald-600' },
    { label: 'Net Book Value', value: fmt(data.total_nbv), icon: TrendingDown, color: 'text-blue-600' },
    { label: 'Assigned', value: data.assigned_count || 0, icon: UserCheck, color: 'text-violet-600', action: () => onNavigate({ tab: 'register', status: 'assigned' }) },
    { label: 'In Storage', value: data.in_storage_count || 0, icon: Building2, color: 'text-amber-600', action: () => onNavigate({ tab: 'register', status: 'in_storage' }) },
    { label: 'In Maintenance', value: data.in_maintenance_count || 0, icon: Wrench, color: 'text-orange-600', action: () => onNavigate({ tab: 'register', status: 'in_maintenance' }) },
    { label: 'Depr. This Month', value: fmt(data.depreciation_this_month), icon: BarChart3, color: 'text-rose-600' },
    { label: 'Added This Month', value: data.added_this_month || 0, icon: Plus, color: 'text-teal-600', action: () => onNavigate({ tab: 'register' }) },
  ];

  const statusData = (data.by_status || []).map((s, i) => ({
    name: STATUS_LABELS[s.status] || s.status, value: s.count, fill: PIE_COLORS[i % PIE_COLORS.length], rawStatus: s.status,
  }));

  const classData = (data.by_class || []).map(c => ({
    name: c.class_code, count: c.count, value: c.value || 0,
  }));

  const handlePieClick = (_, idx) => {
    const entry = statusData[idx];
    if (entry?.rawStatus) onNavigate({ tab: 'register', status: entry.rawStatus });
  };

  const handleClassClick = (entry) => {
    if (entry?.name) onNavigate({ tab: 'register', class_code: entry.name });
  };

  return (
    <div className="space-y-6" data-testid="asset-dashboard">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} onClick={k.action} data-testid={`asset-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`border rounded-xl p-4 space-y-1.5 hover:shadow-sm transition-shadow ${k.action ? 'cursor-pointer hover:border-primary/40' : ''}`}>
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-lg bg-muted ${k.color}`}><k.icon className="h-4 w-4" /></div>
              <span className="text-xs font-medium text-muted-foreground">{k.label}</span>
            </div>
            <p className="text-2xl font-bold tracking-tight">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status pie chart */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Assets by Status</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a segment to filter the register</p>
          {statusData.length === 0 ? <p className="text-xs text-muted-foreground py-8 text-center">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({ name, value }) => `${name}: ${value}`} className="cursor-pointer"
                  onClick={handlePieClick}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By class bar chart */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Assets by Class</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a bar to filter the register</p>
          {classData.length === 0 ? <p className="text-xs text-muted-foreground py-8 text-center">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={classData} layout="vertical" margin={{ left: 60, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={55} />
                <Tooltip formatter={(v, n) => n === 'value' ? `$${v.toLocaleString()}` : v} />
                <Bar dataKey="count" fill="#4f46e5" radius={[0, 4, 4, 0]} name="Count" className="cursor-pointer"
                  onClick={(entry) => handleClassClick(entry)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Value by class */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Acquisition Value by Class</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a bar to filter by class</p>
          {classData.length === 0 ? <p className="text-xs text-muted-foreground py-8 text-center">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={classData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} name="Value" className="cursor-pointer"
                  onClick={(entry) => handleClassClick(entry)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By site */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Assets by Site</h3>
          {(data.by_site || []).length === 0 ? <p className="text-xs text-muted-foreground py-8 text-center">No data</p> : (
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {(data.by_site || []).map((s, i) => (
                <div key={s.site_code} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => onNavigate({ tab: 'register', site_code: s.site_code })}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-sm">{s.site_code || 'Unassigned'}</span>
                  </div>
                  <Badge variant="outline">{s.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  DIALOGS
// ═══════════════════════════════════════════════════════════════════════

function ClassFormDialog({ initial, onClose, onSaved }) {
  const isEdit = !!initial.class_code;
  const [form, setForm] = useState({
    class_code: initial.class_code || '',
    class_name: initial.class_name || '',
    description: initial.description || '',
    asset_type: initial.asset_type || 'fixed_asset',
    depreciation_method: initial.depreciation_method || 'straight_line',
    useful_life_months: initial.useful_life_months || 36,
    residual_value_type: initial.residual_value_type || 'none',
    residual_value_default: initial.residual_value_default || 0,
    capitalization_threshold: initial.capitalization_threshold || 0,
    requires_serial_number: initial.requires_serial_number || false,
    requires_assignment: initial.requires_assignment || false,
    active: initial.active !== undefined ? initial.active : true,
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/assets/classes/${initial.class_code}`, form);
      } else {
        await api.post('/api/assets/classes', form);
      }
      toast.success(isEdit ? 'Class updated' : 'Class created');
      onSaved();
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="class-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Asset Class' : 'New Asset Class'}</DialogTitle><DialogDescription>Configure class properties and depreciation rules.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Class Code</Label>
            <Input value={form.class_code} onChange={e => f('class_code', e.target.value)} disabled={isEdit} placeholder="e.g. IT-EQUIP" data-testid="class-code-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Class Name</Label>
            <Input value={form.class_name} onChange={e => f('class_name', e.target.value)} placeholder="e.g. IT Equipment" data-testid="class-name-input" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={e => f('description', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Asset Type</Label>
            <select value={form.asset_type} onChange={e => f('asset_type', e.target.value)} className="h-9 w-full rounded-lg border px-2 text-sm bg-background" data-testid="class-type-select">
              <option value="fixed_asset">Fixed Asset</option>
              <option value="current_asset">Current Asset</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Depreciation Method</Label>
            <select value={form.depreciation_method} onChange={e => f('depreciation_method', e.target.value)} className="h-9 w-full rounded-lg border px-2 text-sm bg-background" data-testid="class-depr-method">
              <option value="straight_line">Straight Line</option>
              <option value="reducing_balance">Reducing Balance</option>
              <option value="none">None</option>
            </select>
          </div>
          {form.depreciation_method !== 'none' && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Useful Life (months)</Label>
                <Input type="number" value={form.useful_life_months} onChange={e => f('useful_life_months', parseInt(e.target.value) || 0)} data-testid="class-useful-life" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Residual Value Type</Label>
                <select value={form.residual_value_type} onChange={e => f('residual_value_type', e.target.value)} className="h-9 w-full rounded-lg border px-2 text-sm bg-background">
                  <option value="none">None</option>
                  <option value="percentage">Percentage</option>
                  <option value="amount">Amount</option>
                </select>
              </div>
              {form.residual_value_type !== 'none' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Residual Value Default {form.residual_value_type === 'percentage' ? '(%)' : '($)'}</Label>
                  <Input type="number" value={form.residual_value_default} onChange={e => f('residual_value_default', parseFloat(e.target.value) || 0)} />
                </div>
              )}
            </>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Capitalization Threshold ($)</Label>
            <Input type="number" value={form.capitalization_threshold} onChange={e => f('capitalization_threshold', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="flex items-center gap-3 pt-4">
            <Switch checked={form.requires_serial_number} onCheckedChange={v => f('requires_serial_number', v)} />
            <Label className="text-xs">Requires Serial Number</Label>
          </div>
          <div className="flex items-center gap-3 pt-4">
            <Switch checked={form.requires_assignment} onCheckedChange={v => f('requires_assignment', v)} />
            <Label className="text-xs">Requires Assignment</Label>
          </div>
          {isEdit && (
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
              <Label className="text-xs">Active</Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="class-save-btn">{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CreateAssetDialog({ classes, onClose, onCreated }) {
  const [form, setForm] = useState({
    class_code: '', description: '', acquisition_cost: '',
    acquisition_date: new Date().toISOString().slice(0, 10),
    in_service_date: new Date().toISOString().slice(0, 10),
    serial_number: '', tag_number: '', site_code: '', site_name: '', location: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const selectedClass = classes.find(c => c.class_code === form.class_code);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/api/assets', { ...form, acquisition_cost: parseFloat(form.acquisition_cost) || 0 });
      toast.success('Asset created');
      onCreated();
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Create failed')); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="create-asset-dialog">
        <DialogHeader><DialogTitle>Create Asset</DialogTitle><DialogDescription>Enter asset details. Depreciation will be calculated from the selected class.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto py-2">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Asset Class *</Label>
            <select value={form.class_code} onChange={e => f('class_code', e.target.value)}
              className="h-9 w-full rounded-lg border px-2 text-sm bg-background" data-testid="asset-class-select">
              <option value="">Select class...</option>
              {classes.filter(c => c.active).map(c => <option key={c.class_code} value={c.class_code}>{c.class_name} ({c.class_code})</option>)}
            </select>
            {selectedClass && (
              <p className="text-[10px] text-muted-foreground">
                {DEPR_LABELS[selectedClass.depreciation_method] || 'No depreciation'}
                {selectedClass.useful_life_months ? ` over ${selectedClass.useful_life_months}mo` : ''}
                {selectedClass.residual_value_type !== 'none' && ` | Residual: ${selectedClass.residual_value_type === 'percentage' ? `${selectedClass.residual_value_default}%` : `$${selectedClass.residual_value_default}`}`}
              </p>
            )}
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Description *</Label>
            <Input value={form.description} onChange={e => f('description', e.target.value)} placeholder="e.g. Dell Latitude 7440" data-testid="asset-desc-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Acquisition Cost *</Label>
            <Input type="number" value={form.acquisition_cost} onChange={e => f('acquisition_cost', e.target.value)} placeholder="0.00" data-testid="asset-cost-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Acquisition Date</Label>
            <Input type="date" value={form.acquisition_date} onChange={e => f('acquisition_date', e.target.value)} data-testid="asset-acq-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">In-Service Date</Label>
            <Input type="date" value={form.in_service_date} onChange={e => f('in_service_date', e.target.value)} data-testid="asset-service-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Serial Number</Label>
            <Input value={form.serial_number} onChange={e => f('serial_number', e.target.value)} data-testid="asset-serial-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tag Number</Label>
            <Input value={form.tag_number} onChange={e => f('tag_number', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Site</Label>
            <Input value={form.site_name} onChange={e => f('site_name', e.target.value)} placeholder="e.g. Headquarters" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Location</Label>
            <Input value={form.location} onChange={e => f('location', e.target.value)} placeholder="e.g. Floor 3, Room 301" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={e => f('notes', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.class_code} data-testid="asset-create-save-btn">{saving ? 'Creating...' : 'Create Asset'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BulkCreateDialog({ classes, onClose, onCreated }) {
  const [form, setForm] = useState({
    class_code: '', description: '', acquisition_cost: '',
    acquisition_date: new Date().toISOString().slice(0, 10),
    in_service_date: new Date().toISOString().slice(0, 10),
    site_name: '', quantity: 2, mode: 'individual',
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/api/assets/bulk', { ...form, acquisition_cost: parseFloat(form.acquisition_cost) || 0 });
      toast.success(`Created ${r.data.count} asset(s)`);
      onCreated();
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Bulk create failed')); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="bulk-create-dialog">
        <DialogHeader><DialogTitle>Bulk Create Assets</DialogTitle><DialogDescription>Create multiple assets at once (e.g. laptops, chairs).</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto py-2">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Asset Class *</Label>
            <select value={form.class_code} onChange={e => f('class_code', e.target.value)}
              className="h-9 w-full rounded-lg border px-2 text-sm bg-background" data-testid="bulk-class-select">
              <option value="">Select class...</option>
              {classes.filter(c => c.active).map(c => <option key={c.class_code} value={c.class_code}>{c.class_name}</option>)}
            </select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={e => f('description', e.target.value)} placeholder="e.g. Dell Monitor U2723QE" data-testid="bulk-desc-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Cost per Unit</Label>
            <Input type="number" value={form.acquisition_cost} onChange={e => f('acquisition_cost', e.target.value)} data-testid="bulk-cost-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Quantity</Label>
            <Input type="number" min={1} max={500} value={form.quantity} onChange={e => f('quantity', parseInt(e.target.value) || 1)} data-testid="bulk-qty-input" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Creation Mode</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="mode" value="individual" checked={form.mode === 'individual'} onChange={e => f('mode', e.target.value)} />
                Individual assets (one record per unit)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="mode" value="bulk_single" checked={form.mode === 'bulk_single'} onChange={e => f('mode', e.target.value)} />
                Single bulk record
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Acquisition Date</Label>
            <Input type="date" value={form.acquisition_date} onChange={e => f('acquisition_date', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Site</Label>
            <Input value={form.site_name} onChange={e => f('site_name', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.class_code} data-testid="bulk-create-save-btn">
            {saving ? 'Creating...' : `Create ${form.mode === 'individual' ? form.quantity : 1} Asset(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ActionDialog({ type, asset, onClose, onDone }) {
  const [form, setForm] = useState({ user_id: '', user_name: '', site_code: '', site_name: '', cost_centre: '', reason: '', disposal_type: 'scrapped', disposal_date: new Date().toISOString().slice(0, 10), disposal_proceeds: 0 });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const titles = { assign: 'Assign Asset', transfer: 'Transfer Asset', return: 'Return to Storage', maintenance: 'Mark In Maintenance', dispose: 'Dispose / Write Off' };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/api/assets/${asset.asset_number}/${type}`, form);
      toast.success(`Asset ${type === 'dispose' ? 'disposed' : type + 'ed'} successfully`);
      onDone();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Action failed')); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid={`action-dialog-${type}`}>
        <DialogHeader><DialogTitle>{titles[type]}</DialogTitle><DialogDescription>Asset: {asset.asset_number} — {asset.description}</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          {(type === 'assign' || type === 'transfer') && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">User Name</Label>
                <Input value={form.user_name} onChange={e => f('user_name', e.target.value)} placeholder="e.g. Jane Doe" data-testid="action-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">User ID</Label>
                <Input value={form.user_id} onChange={e => f('user_id', e.target.value)} placeholder="e.g. jdoe" data-testid="action-user-id" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Site</Label>
                <Input value={form.site_name} onChange={e => f('site_name', e.target.value)} placeholder="e.g. West Office" data-testid="action-site" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Cost Centre</Label>
                <Input value={form.cost_centre} onChange={e => f('cost_centre', e.target.value)} data-testid="action-cost-centre" />
              </div>
            </>
          )}
          {(type === 'return' || type === 'maintenance') && (
            <div className="space-y-1.5">
              <Label className="text-xs">Reason</Label>
              <Input value={form.reason} onChange={e => f('reason', e.target.value)} placeholder="Reason..." data-testid="action-reason" />
            </div>
          )}
          {type === 'dispose' && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Disposal Type</Label>
                <select value={form.disposal_type} onChange={e => f('disposal_type', e.target.value)} className="h-9 w-full rounded-lg border px-2 text-sm bg-background" data-testid="action-disposal-type">
                  <option value="sold">Sold</option>
                  <option value="scrapped">Scrapped</option>
                  <option value="written_off">Written Off</option>
                  <option value="returned">Returned to Vendor</option>
                  <option value="donated">Donated</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Disposal Date</Label>
                <Input type="date" value={form.disposal_date} onChange={e => f('disposal_date', e.target.value)} data-testid="action-disposal-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Proceeds ($)</Label>
                <Input type="number" value={form.disposal_proceeds} onChange={e => f('disposal_proceeds', parseFloat(e.target.value) || 0)} data-testid="action-proceeds" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Reason *</Label>
                <Input value={form.reason} onChange={e => f('reason', e.target.value)} placeholder="Required reason for disposal" data-testid="action-dispose-reason" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant={type === 'dispose' ? 'destructive' : 'default'} data-testid="action-confirm-btn">
            {saving ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
